import {
	PERSONA_CONTEXT_MAX_CHARS,
	PERSONA_QUERY_MAX_CHARS,
	type PersonaFields,
	applyPersonaToQuery,
	renderPersonaContext,
} from "@repo/core/client";
import { cn } from "@ui/lib";
import { AlertTriangleIcon } from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";

/**
 * The live "What we send" block: the persona's first-person context with a
 * character count against the 600 limit and, optionally, the exact one-line
 * text a platform receives when the context is combined with a prompt.
 *
 * Pass `fields` to render from structured fields (an editor updates live), or
 * `context` when the text is already rendered (drafts, stored personas).
 * Strings come from `personas.card.preview`.
 */
export function PersonaContextPreview({
	fields,
	context,
	exampleQuery,
	label,
	compact = false,
	showHint = false,
	showCount = true,
	className,
}: {
	fields?: PersonaFields;
	context?: string;
	/** Shows the combined persona + prompt text and its 2,000-character check. */
	exampleQuery?: string | null;
	/** Heading; defaults to "What we send". */
	label?: string;
	/** Tighter spacing and no meter, for cards. */
	compact?: boolean;
	/** One line on how the context reaches the model. */
	showHint?: boolean;
	/** The "N / 600 characters" readout. Off on public (marketing) cards. */
	showCount?: boolean;
	className?: string;
}) {
	const t = useTranslations("personas.card");
	const format = useFormatter();
	const fmt = (n: number) => format.number(n);

	const text = context ?? (fields ? renderPersonaContext(fields) : "");
	const overrideLength = fields?.contextOverride?.trim().length ?? 0;
	const overrideCut = overrideLength > PERSONA_CONTEXT_MAX_CHARS;
	const fill = Math.min(100, (text.length / PERSONA_CONTEXT_MAX_CHARS) * 100);
	const combined = exampleQuery
		? applyPersonaToQuery(text, exampleQuery)
		: null;

	return (
		<div className={cn(compact ? "space-y-1.5" : "space-y-2", className)}>
			<div className="flex items-baseline justify-between gap-3">
				<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					{label ?? t("whatWeSend")}
				</p>
				{showCount ? (
					<p className="shrink-0 text-xs tabular-nums text-muted-foreground">
						{t("preview.characters", {
							count: fmt(text.length),
							max: fmt(PERSONA_CONTEXT_MAX_CHARS),
						})}
					</p>
				) : null}
			</div>
			{compact ? null : (
				<div
					className="h-1 w-full overflow-hidden rounded-full bg-border"
					role="meter"
					aria-label={t("preview.meterLabel")}
					aria-valuemin={0}
					aria-valuemax={PERSONA_CONTEXT_MAX_CHARS}
					aria-valuenow={text.length}
				>
					<div
						className="h-full rounded-full bg-primary"
						style={{ width: `${fill}%` }}
					/>
				</div>
			)}
			<p
				className={cn(
					"rounded-lg border bg-background leading-relaxed text-foreground",
					compact ? "p-2.5 text-xs" : "p-3 text-sm",
				)}
			>
				{text || (
					<span className="text-muted-foreground">
						{t("preview.empty")}
					</span>
				)}
			</p>
			{overrideCut ? (
				<p className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<AlertTriangleIcon className="size-3.5 shrink-0" />
					{t("preview.overrideCut", {
						count: fmt(overrideLength),
						max: fmt(PERSONA_CONTEXT_MAX_CHARS),
					})}
				</p>
			) : null}
			{combined ? (
				<div className="space-y-1.5 pt-1">
					<div className="flex items-baseline justify-between gap-3">
						<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							{t("preview.sentWithPrompt")}
						</p>
						<p className="shrink-0 text-xs tabular-nums text-muted-foreground">
							{t("preview.combinedCount", {
								count: fmt(combined.length),
								max: fmt(PERSONA_QUERY_MAX_CHARS),
							})}
						</p>
					</div>
					<p
						className={cn(
							"rounded-lg border border-dashed leading-relaxed",
							compact ? "p-2.5 text-xs" : "p-3 text-sm",
						)}
					>
						<span className="text-muted-foreground">
							{combined.text.slice(0, combined.queryStart)}
						</span>
						<span className="font-medium text-foreground">
							{combined.text.slice(combined.queryStart)}
						</span>
					</p>
					{combined.fits ? null : (
						<p className="flex items-center gap-1.5 text-xs text-muted-foreground">
							<AlertTriangleIcon className="size-3.5 shrink-0" />
							{t("preview.overLimit", {
								max: fmt(PERSONA_QUERY_MAX_CHARS),
							})}
						</p>
					)}
				</div>
			) : null}
			{showHint ? (
				<p className="text-xs text-muted-foreground">
					{t("preview.hint")}
				</p>
			) : null}
		</div>
	);
}
